import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../../../../i18n/useLanguage";
import { HashLink } from "../../../../../navigation/HashLink";
import { useAddLineItemToCart } from "../../../../../queries/useAddLineItemToCart";
import { useRemoveLineItemFromCart } from "../../../../../queries/useRemoveLineItemFromCart";
import {
  AnyOrderItem,
  IncludedOptionalLineItem,
  OptionalLineItem,
} from "../../../../../stores/slices/cartSlice";
import { Button } from "../../../../../ui/Button/Button";
import { KeyValue } from "../../../../../ui/KeyValue/KeyValue";
import { PriceDisplay } from "../../../../../ui/PriceDisplay/PriceDisplay";
import { CartIcon } from "../../../../../ui/icons/CartIcon";
import { TrashIcon } from "../../../../../ui/icons/TrashIcon";
import { getPriceMatchDilalogLink } from "../PriceMatchDialog/getPriceMatchDilalogLink";
import { LineItemName } from "./LineItemName";

type AdditionalItemProps = {
  isOrder: boolean;
  lineItem: IncludedOptionalLineItem | OptionalLineItem;
  orderItem: AnyOrderItem;
};

export const AdditionalItem: FunctionComponent<AdditionalItemProps> = ({
  isOrder,
  lineItem,
  orderItem,
}) => {
  const { t } = useTranslation();
  const [language] = useLanguage();
  const isInCart = "lineItemDetails" in lineItem;
  const isPriceMatch = Number(lineItem.isPriceMatch) === 1;
  const addLineItemToCart = useAddLineItemToCart();
  const removeLineItemFromCart = useRemoveLineItemFromCart();
  return (
    <div className="grid grid-cols-4 border px-4 py-2 lg:py-3 rounded-section items-center">
      <strong className="font-medium col-span-3 md:col-span-1">
        <LineItemName item={lineItem} language={language} />
      </strong>
      <KeyValue className="hidden md:block">
        <>{t("cart.price")}:</>
        <PriceDisplay>
          {Number(lineItem.price) / Number(lineItem.qty)}
        </PriceDisplay>
      </KeyValue>
      <KeyValue className="hidden md:block">
        <>{t("cart.quantity")}:</>
        <>{lineItem.qty}</>
      </KeyValue>
      <span className="flex gap-1 items-center justify-between">
        <KeyValue>
          <span className="hidden md:block">{t("cart.totalPrice")}:</span>
          <>
            <PriceDisplay>{isInCart ? lineItem.price : 0}</PriceDisplay>
          </>
        </KeyValue>
        {!isOrder &&
          (isInCart ? (
            <Button
              onClick={(): void => {
                removeLineItemFromCart.send(orderItem, lineItem);
              }}
              aria-label="removeLineItemFromCart"
              busy={removeLineItemFromCart.loading}
              className="h-6 min-h-0 px-2 py-1"
            >
              <TrashIcon />
              <span className="hidden lg:block">{t("cart.remove")}</span>
            </Button>
          ) : (
            <>
              {isPriceMatch ? (
                <HashLink hash={getPriceMatchDilalogLink(lineItem.lineItemId)}>
                  <Button
                    aria-label="addLineItemToCart"
                    className="h-6 min-h-0 px-2 py-1"
                    disabled={addLineItemToCart.loading}
                  >
                    <CartIcon />
                    <span className="hidden lg:block">{t("cart.add")}</span>
                  </Button>
                </HashLink>
              ) : (
                <Button
                  onClick={(): void => {
                    addLineItemToCart.send(orderItem, lineItem);
                  }}
                  aria-label="addLineItemToCart"
                  busy={addLineItemToCart.loading}
                  className="h-6 min-h-0 px-2 py-1"
                >
                  <CartIcon />
                  <span className="hidden lg:block">{t("cart.add")}</span>
                </Button>
              )}
            </>
          ))}
      </span>
    </div>
  );
};
